import {
	get_mode,
	isThreeEM,
	is_gen_2,
	isGen2EM,
	get_gen_2_component_channel_structure,
	get_gen_2_component_from_category,
} from './interface_helpers';
import { convert } from './utilities';

//Gets proper EM component for the device's status.
// - em component is used for 3-phase emeters
// - em1 component is used for single-phase emeters and 3-phase emeters in monophase mode
// This handles old firmware versions where either em or em1 component is used for all emeters.
// 'monophase' as device profile is used in the modern adaptation.
export const get_em_component = (device, status, settings) => {
	let component = '';
	if (get_mode(device, status, settings) == 'monophase') {
		component = 'em1';
	} else if (typeof status['em:' + device?.channel] !== 'undefined') {
		component = 'em';
	} else if (typeof status['em1:' + device?.channel] !== 'undefined') {
		component = 'em1';
	}
	return component;
};

export const emeter_status_configurator = (status, device, settings?) => {
	let interface_data = [];
	let component = get_em_component(device, status, settings);
	let total = '';

	if (!component) return [];
	if (component == 'em') {
		total = 'total_';
	}

	const statusProp = `${component}:${device.channel}`;
	const emStatus = status[`${statusProp}`] || {};

	interface_data = [
		{
			name: 'power',
			icon: getIcon('act_power'),
			value: total
				? convert(emStatus[`${total}act_power`], getUnit('act_power'))
				: convert(
						Object.keys(emStatus)
							.filter((k) => k.indexOf('act_power') !== -1)
							.map((k) => emStatus[k])
							.reduce((a, b) => a + b, 0),
						getUnit('act_power'),
					),
		},
	];
	if (emStatus.voltage != null) {
		interface_data.push({
			name: 'voltage',
			icon: getIcon('voltage'),
			value: `${emStatus.voltage} ${getUnit('voltage')}`,
		});
	}
	if (emStatus[`${total}current`] != null) {
		interface_data.push({
			name: 'current',
			icon: getIcon('current'),
			value: total
				? convert(emStatus[`${total}current`], getUnit('current'))
				: convert(
						Object.keys(emStatus)
							.filter((k) => k.indexOf('current') !== -1)
							.map((k) => emStatus[k])
							.reduce((a, b) => a + b, 0),
						getUnit('current'),
					),
			advLabel: true,
		});
	}
	if (emStatus[`${total}aprt_power`] != null) {
		interface_data.push({
			name: 'aprt_power',
			icon: getIcon('aprt_power'),
			value: total
				? convert(emStatus[`${total}aprt_power`], getUnit('aprt_power'))
				: convert(
						Object.keys(emStatus)
							.filter((k) => k.indexOf('aprt_power') !== -1)
							.map((k) => emStatus[k])
							.reduce((a, b) => a + b, 0),
						getUnit('aprt_power'),
					),
			advLabel: true,
		});
	}
	if (isThreeEM(device?.type) && device?.mode != 'monophase') {
		const emTotalStatus = status['emdata:0'] || {};
		const emData = { ...emTotalStatus, ...emStatus };
		Object.keys(emData)
			.sort()
			.forEach((i) => {
				if (/^(total_act|total_act_ret)$|([abc]_)/.test(i)) {
					const key = (/^[abc]_/.test(i) ? i.substring(2) : i).replace(/_energy$/, '');
					const unit = getUnit(key);
					let value = emData[i];
					if (isNaN(value) || value == null || (/_pf$/.test(key) && value == 0)) {
						value = 'N/A ' + unit;
					} else {
						value = /(total|current|power)/.test(i)
							? convert(value, unit)
							: `${value} ${unit}`;
					}
					interface_data.push({
						name: i.replace(/_energy$/, ''),
						icon: getIcon(key),
						value,
						advLabel: true,
					});
				}
			});
	} else {
		if (status?.[`em1data:${device.channel}`]?.total_act_energy != null) {
			interface_data.push({
				name: 'total_act',
				icon: getIcon('total_act'),
				value: convert(
					status[`em1data:${device.channel}`].total_act_energy,
					getUnit('total_act'),
				),
				advLabel: true,
			});
		}
		if (status?.[`em1data:${device.channel}`]?.total_act_ret_energy != null) {
			interface_data.push({
				name: 'total_act_ret',
				icon: getIcon('total_act_ret'),
				value: convert(
					status[`em1data:${device.channel}`].total_act_ret_energy,
					getUnit('total_act_ret'),
				),
				advLabel: true,
			});
		}
		if (emStatus.pf != null) {
			interface_data.push({
				name: 'pf',
				icon: getIcon('pf'),
				value: emStatus.pf == 0 ? 'N/A' : emStatus.pf,
				advLabel: true,
			});
		}
		if (emStatus.freq != null) {
			interface_data.push({
				name: 'freq',
				icon: getIcon('freq'),
				value: `${emStatus.freq} ${getUnit('freq')}`,
				advLabel: true,
			});
		}
		// Daisy active_tariff is shown on the dedicated Tariffs sidepanel tab, not Main labels.
	}
	return interface_data;
};

export const em_g1_status_configurator = (status, device) => {
	let interface_data = [];
	const emStatus = status.emeters[device.channel] || {};
	interface_data = [
		{
			name: 'power',
			icon: getIcon('act_power'),
			value: convert(emStatus.power, getUnit('act_power')),
		},
		{
			name: 'voltage',
			icon: getIcon('voltage'),
			value: `${emStatus.voltage} ${getUnit('voltage')}`,
		},
		{
			name: 'total',
			icon: getIcon('total'),
			value: convert(emStatus.total, getUnit('total')),
			advLabel: true,
		},
		{
			name: 'total_returned',
			icon: getIcon('total_returned'),
			value: convert(emStatus.total_returned, getUnit('total_returned')),
			advLabel: true,
		},
		{
			name: 'reactive',
			icon: getIcon('reactive'),
			value: `${emStatus.reactive} ${getUnit('reactive')}`,
			advLabel: true,
		},
		{
			name: 'power_factor',
			icon: getIcon('pf'),
			value: emStatus.pf == 0 ? 'N/A' : emStatus.pf,
			advLabel: true,
		},
	];
	return interface_data;
};

export const em3_g1_status_configurator = (status) => {
	let interface_data = [];
	const emStatus = status.emeters || [];
	interface_data = [
		{
			name: 'power',
			icon: getIcon('power'),
			value: convert(
				emStatus.map((i) => i.power).reduce((a, b) => a + b, 0),
				getUnit('power'),
			),
		},
		{
			name: 'total',
			icon: getIcon('total'),
			value: convert(
				emStatus.map((i) => i.total).reduce((a, b) => a + b, 0),
				getUnit('total'),
			),
			advLabel: true,
		},
		{
			name: 'total_returned',
			icon: getIcon('total_returned'),
			value: convert(
				emStatus.map((i) => i.total_returned).reduce((a, b) => a + b, 0),
				getUnit('total_returned'),
			),
			advLabel: true,
		},
	];
	let phase = 'a';
	emStatus.forEach((c) => {
		Object.keys(c).forEach((i) => {
			if (/^(power|current|voltage|pf|total|total_returned)$/.test(i)) {
				const unit = getUnit(i);
				let value = c[i];
				if (isNaN(value) || value == null || (i == 'pf' && value == 0)) {
					value = 'N/A ' + unit;
				} else {
					value = /^(voltage|pf)$/.test(i) ? `${value} ${unit}` : convert(value, unit);
				}
				interface_data.push({
					name: `${phase}_${i}`,
					icon: getIcon(i),
					value,
					advLabel: true,
				});
			}
		});
		phase = phase == 'a' ? 'b' : 'c';
	});
	return interface_data;
};

export const getUnit = (type: string) => {
	let unit = 'W';
	switch (type) {
		case 'voltage':
			unit = 'V';
			break;
		case 'total':
		case 'total_returned':
		case 'total_act':
		case 'total_act_ret':
		case 'total_act_energy':
			unit = 'Wh';
			break;
		case 'aprt_power':
			unit = 'VA';
			break;
		case 'current':
		case 'total_current':
			unit = 'A';
			break;
		case 'freq':
			unit = 'Hz';
			break;
		case 'pf':
			unit = '';
			break;
		case 'reactive':
			unit = 'VAR';
			break;
		case 'active_tariff':
			unit = '';
			break;
	}
	return unit;
};

export const getIcon = (type: string) => {
	let icon = 'fas fa-bolt';
	switch (type) {
		case 'voltage':
			icon = 'fas fa-circle-bolt';
			break;
		case 'aprt_power':
		case 'total_act_energy':
			icon = 'fa fa-bolt-auto';
			break;
		case 'current':
		case 'total_current':
			icon = 'fas fa-plug-circle-bolt';
			break;
		case 'pf':
			icon = 'fas fa-wave-pulse';
			break;
		case 'freq':
			icon = 'fas fa-wave-square';
			break;
		case 'active_tariff':
			icon = 'fas fa-tags';
			break;
	}
	return icon;
};

/**
 * Resolves Gen1 emeter status rows for SHEM / SHEM-3.
 * Returns an empty array when status is not ready so the drawer can render without throwing.
 *
 * @param device - Active device (channel is used when the device is split)
 * @param status - Device status object that may still be loading
 * @param isSplit - Whether the 3EM is split into per-channel devices
 * @returns Emeter status items to render, or an empty array
 */
export const getGen1EmeterItems = (
	device: { channel?: number } | undefined,
	status: { emeters?: unknown[] } | undefined,
	isSplit = false,
) => {
	const emeters = status?.emeters;
	if (!Array.isArray(emeters)) {
		return [];
	}

	if (isSplit) {
		const channelItem = emeters[device?.channel];
		return channelItem ? [channelItem] : [];
	}

	return emeters;
};

/**
 * This helper works only for Gen1 emeter devices that use EmeterDataG1 component (SHEM-3 and SHEM).
 * Returns an array of field names displayed by EmeterDataG1 component that should be filtered out from LabelList to prevent duplication.
 *
 * @param {Device} device - The device object
 * @param {any} status - The device status object
 * @returns {string[]} Array of field names to filter out
 */
export const getEmeterDataG1DisplayedFields = (device, status) => {
	if (!device || !status || !status.emeters) {
		return [];
	}

	if (device?.type !== 'SHEM-3' && device?.type !== 'SHEM') {
		return [];
	}

	const isArray = Array.isArray(status.emeters);
	const items = isArray ? status.emeters : [];

	const fieldsToFilter = [];

	// For SHEM, always treat as single-phase (em_g1_status_configurator always returns fields without prefixes)
	// For SHEM-3, check if multi-phase (em3_g1_status_configurator returns prefixed fields for multi-phase)
	if (device?.type === 'SHEM') {
		// SHEM always uses em_g1_status_configurator which returns fields without phase prefixes
		fieldsToFilter.push('power', 'voltage', 'total', 'total_returned', 'power_factor', 'reactive');
	} else if (device?.type === 'SHEM-3') {
		const isMultiPhase = items && items.length > 1;

		if (isMultiPhase) {
			fieldsToFilter.push('power', 'total', 'total_returned');

			const phases = ['a', 'b', 'c'];
			const perPhaseFields = ['power', 'voltage', 'current', 'pf', 'total', 'total_returned'];

			phases.forEach((phase) => {
				perPhaseFields.forEach((field) => {
					fieldsToFilter.push(`${phase}_${field}`);
				});
			});
		} else {
			fieldsToFilter.push(
				'power',
				'voltage',
				'total',
				'total_returned',
				'power_factor',
				'reactive',
			);
		}
	}

	return fieldsToFilter;
};

/**
 * This helper works only for Gen2 emeter devices that use EmeterAdvancedView component.
 * Returns an array of field names displayed by EmeterDataG2Phases/EmeterDataG2 components that should be filtered out from LabelList to prevent duplication.
 *
 * The function checks which component is used (EmeterDataG2Phases for Gen2EM/monophase, EmeterDataG2 for multi-phase)
 * and returns the appropriate field names that match what's displayed in parameter cards.
 *
 * @param {Device} device - The device object
 * @param {any} status - The device status object
 * @returns {string[]} Array of field names to filter out
 */
export const getEmeterDataG2DisplayedFields = (device, status) => {
	if (!device || !status) {
		return [];
	}

	// Only works for Gen2 emeter devices
	if (!is_gen_2(device) || device?.category !== 'emeter') {
		return [];
	}

	const fieldsToFilter = [];

	// Determine which component is used based on device type
	const isGen2EMDevice = isGen2EM(device.type) || device.mode === 'monophase';

	if (isGen2EMDevice) {
		// EmeterDataG2Phases component displays:
		// - act_power (mapped to 'power' in status_configurator)
		// - aprt_power
		// - current (conditionally)
		// - voltage
		// - pf (power factor)
		fieldsToFilter.push('power', 'aprt_power', 'voltage', 'pf', 'current');
	} else {
		// EmeterDataG2 component for multi-phase devices
		// This component displays phase-specific data and totals
		// Common fields that appear in parameter cards:
		fieldsToFilter.push('power', 'aprt_power', 'voltage', 'pf', 'current');

		// Multi-phase devices may also show total values
		const emData = status?.[`${get_gen_2_component_from_category(device)}data:${device?.channel}`];
		if (emData?.total_act_ret !== undefined) {
			fieldsToFilter.push('total_act_ret');
		}
		if (emData?.total_act !== undefined) {
			fieldsToFilter.push('total_act');
		}
	}

	return fieldsToFilter;
};
